import TableView, { type TableSection } from '@/components/TableView'
import { HStack } from '@/components/ui/hstack'
import { IconSymbol } from '@/components/ui/IconSymbol'
import { Image } from '@/components/ui/image'
import { Text } from '@/components/ui/text'
import { VStack } from '@/components/ui/vstack'
import usePlatformColor from '@/lib/hooks/usePlatformColor'
import type { TradeBuyer, TradeOrderDetail } from '@/typings/trade'
import { View } from 'react-native'
import {
  formatAmount,
  formatBuyerAddress,
  formatDateTime,
  formatPlainAmount,
  PAID_STATUS_META,
  REFUND_STATUS_LABEL,
} from './tradeFormat'

type Props = {
  order: TradeOrderDetail
  buyer: TradeBuyer | null
  refreshing: boolean
  onRefresh: () => void
}

type InfoRow = { title: string; detailText: string | null | undefined }

// Hex twins of the system colors; an SF Symbol's tint takes a plain color string.
const STATUS_HEX = {
  systemGreen: '#34C759',
  systemOrange: '#FF9500',
  systemRed: '#FF3B30',
  secondaryLabel: '#8E8E93',
} as const

const REFUND_NOTICE = '如需退款或添加订单备注，请在电脑端登录金数据操作。'

export default function TradeOrder({ order, buyer, refreshing, onRefresh }: Props) {
  const sections: TableSection[] = [
    {
      title: '买家信息',
      rows: visible([
        { title: '姓名', detailText: buyer?.name },
        { title: '手机号', detailText: buyer?.mobile },
        { title: '邮箱', detailText: buyer?.email },
        { title: '地址', detailText: formatBuyerAddress(buyer?.address) },
      ]),
    },
    {
      title: '交易信息',
      rows: visible([
        { title: '交易号', detailText: order.tradeNo },
        { title: '商户订单号', detailText: order.merchantOutTradeNo },
        { title: '金数据单号', detailText: order.outTradeNo },
        { title: '商户号', detailText: order.merchantNo },
        { title: '下单时间', detailText: formatDateTime(order.orderedAt) },
        { title: '支付时间', detailText: formatDateTime(order.paidAt) },
      ]),
    },
    {
      title: '退款记录',
      headerDetailText: `剩余可退：${formatAmount(order.amount - order.refundedAmount)}`,
      rows: order.refunds.map(refund => ({
        title: `${REFUND_STATUS_LABEL[refund.status] ?? refund.status} -${formatAmount(refund.amount)}`,
        detailText: formatDateTime(refund.refundedAt ?? refund.createdAt),
        detailTextBelow: true,
      })),
    },
  ].filter(section => section.rows.length > 0)

  const lastSection = sections[sections.length - 1]
  if (lastSection) lastSection.footer = REFUND_NOTICE

  return (
    <TableView tableViewStyle="insetGrouped" sections={sections} refreshing={refreshing} onRefresh={onRefresh}>
      <View>
        <TradeOrderSummary order={order} />
      </View>
    </TableView>
  )
}

function visible(rows: InfoRow[]) {
  return rows.filter(row => row.detailText).map(row => ({ ...row, detailTextBelow: true, copyValue: row.detailText }))
}

/** Status, form name and line items, shown as the table header above the info sections. */
function TradeOrderSummary({ order }: { order: TradeOrderDetail }) {
  const [orangeColor] = usePlatformColor('systemOrange')

  const meta = PAID_STATUS_META[order.paidStatus]
  const statusColor = STATUS_HEX[meta?.color ?? 'secondaryLabel']
  const isQuickpay = order.sourceEntry.formScene === 'quickpay'

  return (
    <View className="px-[20px] pt-2">
      <HStack space="xs" className="items-center">
        <IconSymbol name={meta?.icon ?? 'info.circle'} size={22} color={statusColor} />
        <Text className="text-title-3 font-semibold" style={{ color: statusColor }}>
          {meta?.label}
        </Text>
      </HStack>
      {order.sourceEntry.formName ? (
        <Text className="leading-[31px] font-bold text-label" style={{ fontSize: 22, marginTop: 8 }}>
          {order.sourceEntry.formName}
        </Text>
      ) : null}
      {order.mockPaid && (
        <Text size="footnote" className="mt-1" style={{ color: orangeColor }}>
          模拟支付，不产生真实交易
        </Text>
      )}

      {/* Points measured from the design; Tailwind spacing here is rem-scaled and comes out smaller */}
      <View
        className="bg-secondary-system-grouped-background"
        style={{ flexDirection: 'column', borderRadius: 18, padding: 16, paddingTop: 14, gap: 12, marginTop: 14 }}
      >
        {order.items.map((item, index) => {
          const imageUrl = (isQuickpay && order.sourceEntry.headerImageUrl) || item.goodsImageUrl
          return (
            <HStack key={index} className="items-center" style={{ gap: 12 }}>
              {imageUrl ? (
                <Image source={{ uri: imageUrl }} alt="" size="none" className="w-[44px] h-[44px] rounded-[8px]" />
              ) : (
                <View
                  className="items-center justify-center bg-system-grouped-background"
                  style={{ width: 44, height: 44, borderRadius: 8 }}
                >
                  {/* systemGray is the same hex in light and dark mode */}
                  <IconSymbol name="bag" size={20} color="#8e8e93" />
                </View>
              )}
              <VStack className="flex-1" style={{ gap: 4 }}>
                <Text className="leading-[20px] text-label font-semibold" style={{ fontSize: 15 }} numberOfLines={2}>
                  {item.name}
                </Text>
                {item.specifications ? (
                  <Text className="leading-[20px] text-secondary-label" style={{ fontSize: 14 }}>
                    {item.specifications}
                  </Text>
                ) : null}
              </VStack>
              <VStack className="items-end self-start" style={{ gap: 4 }}>
                <Text className="leading-[20px] text-label font-medium" style={{ fontSize: 15 }}>
                  +{formatPlainAmount(item.unitPrice)}
                </Text>
                <Text className="leading-[20px] text-secondary-label" style={{ fontSize: 14 }}>
                  x{item.quantity}
                </Text>
              </VStack>
            </HStack>
          )
        })}
        <HStack className="items-center justify-between">
          <Text className="leading-[20px] text-label" style={{ fontSize: 14 }}>
            总计
          </Text>
          <Text className="leading-[24px] font-bold text-label" style={{ fontSize: 17 }}>
            {formatAmount(order.amount)}
          </Text>
        </HStack>
      </View>
    </View>
  )
}
